import { Pencil, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * How a category's changes will be handled, stated before the patient commits anything.
 *
 * Only facts that differ from the default are shown at rest: `reception` (the Hausarzt row, whose
 * changes always go to the reception) and `staged` once the patient has queued something. Ordinary
 * contacts carry no idle chip, because "saved directly" is the default and stating it on every row
 * was workflow jargon (KC-54).
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <HandlingChip variant="reception" />
 * ```
 */
export type HandlingChipVariant = "reception" | "staged";

/**
 * The pill on a category row that names what happens to changes in that category, in kiosk
 * text size (KC-09). Wording stays restrained — "prüft", never "verifiziert".
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <HandlingChip variant="staged" stagedCount={2} />
 * ```
 */
export function HandlingChip({
  variant,
  stagedCount = 0,
}: {
  variant: HandlingChipVariant;
  stagedCount?: number;
}) {
  const { t } = useTranslation();

  if (variant === "staged") {
    return (
      <span
        className="inline-flex min-h-8 items-center gap-2 rounded-full bg-primary-subtle px-3 py-1 text-base font-medium text-primary-ink"
        data-testid="patient-info-handling-chip"
        data-variant="staged"
      >
        <Pencil className="size-4" aria-hidden="true" />
        {t("self-service.check-in.patient-info.review.chip-changed", {
          count: stagedCount,
        })}
      </span>
    );
  }

  return (
    <span
      className="inline-flex min-h-8 items-center gap-2 rounded-full bg-brand-supporting-subtle px-3 py-1 text-base font-medium text-brand-supporting-ink"
      data-testid="patient-info-handling-chip"
      data-variant="reception"
    >
      <ShieldAlert className="size-4" aria-hidden="true" />
      {t("self-service.check-in.patient-info.review.chip-reception-v2")}
    </span>
  );
}
